import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaRedo, FaSignOutAlt, FaCalendarAlt, FaDownload, FaListUl } from "react-icons/fa";
import "./IncomeReport.css"; // Re-using the same CSS file for identical styling!
import topLogo from "./logo-placeholder.png"; 

const VyayReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef();

  // --- STATE ---
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [descSelect, setDescSelect] = useState("");
  const [reportData, setReportData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // --- HANDLERS ---
  const handleLogout = () => {
    if (window.confirm("Do you want to Sign Out?")) {
      localStorage.clear();
      navigate("/");
    }
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setDescSelect("");
    setReportData([]);
    setTotalAmount(0);
    setHasSearched(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      alert("कृपया दिनांक से और दिनांक तक का चयन करें। (Please select both dates.)");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      // Pointing to the new expense route
      const response = await fetch("/api/dashboard/expense-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromDate: fromDate,
          toDate: toDate,
          descSelect: descSelect,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setReportData(result.data);
        setTotalAmount(result.total);
      } else {
        alert("Error fetching report: " + result.message);
        handleReset();
      }
    } catch (error) {
      console.error("Search Error:", error);
      alert("Server Connection Error.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN");
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const getCategorySummary = () => {
    const summary = {};
    reportData.forEach((item) => {
      const cat = item.descSelect || "Other";
      summary[cat] = (summary[cat] || 0) + (Number(item.amount) || 0);
    });
    return Object.entries(summary).sort((a, b) => b[1] - a[1]);
  };

  return (
    <div className="income-page-container">
      {/* --- TOP NAV BAR --- */}
      <div className="income-header-nav">
        <div
          className="income-brand"
          onClick={() => navigate("/home")}
          style={{ cursor: "pointer" }}
        >
          <img src={topLogo} alt="Logo" className="income-logo" />
          <div className="income-brand-text">
            <h2>गायत्री परिवार</h2>
            <h3>अलीराजपुर</h3>
          </div>
        </div>
              <button className="income-logout-btn" onClick={handleLogout} title="Sign Out">
    <FaSignOutAlt size={18} /> 
    <span>Sign Out</span>
</button>
      </div>

      {/* --- MAIN CARD --- */}
      <div className="income-main-card" ref={reportRef}>
        <h2 className="income-card-title">
          गायत्री परिवार अलीराजपुर व्यय की रिपोर्ट दिनांक {fromDate} से दिनांक{" "}
          {toDate} तक
        </h2>

        <form onSubmit={handleSearch}>
          <div className="income-input-grid">
            <div className="income-input-group">
              <label>From Date (दिनांक से)</label>
              <div className="income-input-wrapper">
                <FaCalendarAlt className="input-icon" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="income-input-group">
              <label>To Date (दिनांक तक)</label>
              <div className="income-input-wrapper">
                <FaCalendarAlt className="input-icon" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <div className="income-input-group" style={{ marginBottom: "30px" }}>
            <label>विवरण (Description)</label>
            <div className="income-input-wrapper">
              <FaListUl className="input-icon" />
              <select
                value={descSelect}
                onChange={(e) => setDescSelect(e.target.value)}
              >
                <option value="">सभी विवरण (All)</option>
                {/* UPDATE THESE OPTIONS TO MATCH YOUR EXPENSE CATEGORIES IN THE DB */}
                <option value="यज्ञ व पूजन सामग्री व्यय">यज्ञ व पूजन सामग्री व्यय</option>
                <option value="परीवाजक मानदेय राशि">परीवाजक मानदेय राशि</option>
                <option value="भोजनालय व्यय">भोजनालय व्यय</option>
                <option value="गौशाला हेतु दान">गौशाला हेतु दान</option>
                <option value="समारोह आयोजन व्यय">समारोह आयोजन व्यय</option>
                <option value="विधुत व्यय">विधुत व्यय</option>
                <option value="स्टेशनरी व्यय">स्टेशनरी व्यय</option>
                <option value="कार्यालयीन व्यय">कार्यालयीन व्यय</option>
                <option value="ट्रस्ट द्वारा दी गई भेट राशि">ट्रस्ट द्वारा दी गई भेट राशि</option>
                <option value="भवन मर्मद व्यय">भवन मर्मद व्यय</option>
                <option value="कम्प्यूटर व्यय">कम्प्यूटर व्यय</option>
                <option value="परिव्राज चिकित्सा व्यय">परिव्राज चिकित्सा व्यय</option>
                <option value="परिव्रजा व्यय (पेट्रोल ,डीजल )">परिव्रजा व्यय (पेट्रोल ,डीजल )</option>
                <option value="अंकेक्षण शुल्क">अंकेक्षण शुल्क</option>
                <option value="अन्य सामान व्यय">अन्य सामान व्यय</option>
                <option value="मार्ग यात्रा व्यय">मार्ग यात्रा व्यय</option>
              </select>
            </div>
          </div>

          <div className="income-btn-grid">
            <button
              type="submit"
              className="income-action-btn search-btn"
              disabled={isLoading}
            >
              <FaSearch /> {isLoading ? "SEARCHING..." : "SEARCH"}
            </button>
            <button
              type="button"
              className="income-action-btn reset-btn"
              onClick={handleReset}
            >
              <FaRedo /> RESET
            </button>
          </div>
        </form>

        {isLoading && (
          <div className="loader-wrapper">
            <div className="loader-content">
              <div className="premium-progress-container">
                <div className="premium-progress-bar">
                  <div className="premium-progress-shimmer"></div>
                </div>
              </div>
              <div className="loading-status">
                <span className="spinner-dot"></span>
                <p>डाटा प्रोसेस हो रहा है... (Processing...)</p>
              </div>
            </div>
          </div>
        )}

        {!isLoading && hasSearched && reportData.length > 0 && (
          <div className="category-summary-section">
            <h4 className="summary-title">
              व्यय रिपोर्ट श्रेणीवार सारांश (Expense Category Summary)
            </h4>
            <div className="summary-grid">
              {getCategorySummary().map(([category, amount], idx) => (
                <div key={idx} className="summary-item-card">
                  <span className="summary-cat-name">{category}</span>
                  <span className="summary-cat-amount">₹ {amount}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && hasSearched && reportData.length === 0 && (
          <div className="no-data-card">
            <div className="no-data-icon-box">
              <FaSearch size={30} className="floating-icon" />
            </div>
            <h3>क्षमा करें, कोई रिकॉर्ड नहीं मिला</h3>
            <p>
              दिनांक <strong>{formatDate(fromDate)}</strong> से{" "}
              <strong>{formatDate(toDate)}</strong> के बीच कोई व्यय दर्ज नहीं
              है।
            </p>
            <button className="retry-btn" onClick={() => setFromDate("")}>
              दिनांक बदलें (Change Dates)
            </button>
          </div>
        )}

        {!isLoading && hasSearched && reportData.length > 0 && (
          <div className="income-results-area">
            <h4 className="results-title">
              किये गए व्यय का विवरण (Expenditure Details)
            </h4>
            <hr className="results-divider" />

            <div className="total-income-display">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>कुल व्यय (Total Expense):</div>
                <span
                  style={{
                    color: "#e53935",
                    fontWeight: "bold",
                    fontSize: "22px",
                  }}
                >
                  ₹ {totalAmount}
                </span>
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontSize: "15px",
                  color: "#777",
                  marginTop: "8px",
                }}
              >
                व्यय का विवरण दिनांक {formatDate(fromDate)} से दिनांक{" "}
                {formatDate(toDate)} तक
              </div>
            </div>

            <div className="table-responsive">
              <table className="income-clean-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>दिनांक</th>
                    <th>वाउचर/रसीद क्र.</th>
                    <th>प्राप्तकर्ता का नाम (Payee Name)</th>
                    <th>विवरण</th>
                    <th>माध्यम</th>
                    <th style={{ textAlign: "right" }}>राशि (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{formatDate(item.date)}</td>
                      <td>{item.voucher_no}</td>
                      <td>{item.name}</td>
                      <td>
                        <div className="desc-main">{item.descSelect}</div>
                        {item.descText && (
                          <div className="desc-sub">{item.descText}</div>
                        )}
                      </td>
                      <td>{item.paymentMode || "OFFLINE"}</td>
                      <td className="amount-cell" style={{ color: "#e53935" }}>
                        ₹ {item.amount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {hasSearched && reportData.length > 0 && (
        <button
          className="fab-download-btn"
          onClick={handleDownloadPDF}
          title="Print/Download as PDF"
        >
          <FaDownload />
        </button>
      )}
    </div>
  );
};

export default VyayReport;